import type {MetaOptions} from '../connect/ConnectJSInterface/InitAndUpdateOptionsTypes';
import {
  transformHeaders,
  transformStripeHeaders,
} from '../utils/transformHeaders';
import {STRIPE_API_VERSION} from './buildStripeClient';
import type {IHttpRequestParams, IProxiedHttpResponse} from './FrameMessenger';
import type {ConnectJsOptions} from '../connect/ConnectJsOptions';
import type {ConnectElementImportKeys} from '../connect/ConnectJSInterface/ConnectElementList';
import {calculateSupportedLocale} from '../intl/calculateSupportedLocale';

export function getRequestBody(
  body?:
    | ReadableStream
    | string
    | Blob
    | ArrayBuffer
    | ArrayBufferView
    | FormData
    | null,
): string | Uint8Array | undefined {
  if (
    typeof body === 'string' ||
    body === null ||
    body === undefined ||
    body instanceof Uint8Array
  ) {
    return body ?? undefined;
  }

  throw new Error(`Type ${typeof body} is not supported by the data layer`);
}

export type FetchOverrideFn = (
  input: RequestInfo,
  init?: RequestInit,
) => Promise<Response>;

export type FetchDefaultHeaders = {
  stripeVersion: string;
  acceptLanguage?: string;
  stripeAccount?: string;
  stripeLivemode?: boolean;
};

export const getFetchDefaultHeaders = (
  metaOptions: MetaOptions | undefined,
  locale: string | undefined,
): FetchDefaultHeaders => {
  let headers: FetchDefaultHeaders = {
    // If a stripe version isn't specified, we have a default. This prevents us from using the
    // stripe version that the platform has as a default in their configuration
    stripeVersion: STRIPE_API_VERSION,
    acceptLanguage: locale,
    stripeAccount: undefined,
    stripeLivemode: undefined,
  };

  if (metaOptions) {
    headers = {
      ...headers,
      stripeAccount: metaOptions.merchantIdOverride,
      stripeLivemode: metaOptions.livemodeOverride,
    };
  }

  return headers;
};

export const buildFetchOverride = (
  proxyRequest: (
    params: IHttpRequestParams,
    signal?: AbortSignal | null | undefined,
  ) => Promise<IProxiedHttpResponse>,
  connectJsOptions: ConnectJsOptions,
  connectElementKey: ConnectElementImportKeys,
) => {
  const {metaOptions} = connectJsOptions.getValues();
  const fetchOverride: FetchOverrideFn = async (
    input: RequestInfo,
    init?: RequestInit,
  ): Promise<Response> => {
    if (typeof input !== 'string') {
      throw new Error(
        `only url string requests supported, received ${typeof input}`,
      );
    }

    const url: string = input;
    const method = init?.method ?? 'GET';

    // Note: the API supports localizing errors, however it needs a valid locale string (i.e. not 'fr', but 'fr-FR')
    // this is why we use the matchedLocale here, which matches to one of our supported locale values and the current
    // locale we are using in embedded
    const preferredLocale = connectJsOptions.getValues().locale;
    const locale = calculateSupportedLocale(preferredLocale);

    const frameResponse = await proxyRequest(
      {
        url,
        method,
        headers: transformStripeHeaders(
          transformHeaders(init?.headers),
          getFetchDefaultHeaders(metaOptions, locale.matchedLocale),
        ),
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        body: getRequestBody(init?.body as string | undefined),
        connectElementKey,
      },
      init?.signal,
    );

    const responseInit = {
      status: frameResponse.status,
      statusText: frameResponse.statusText,
      headers: frameResponse.headers,
    };
    const response = new Response(
      // TODO: Support more data types besides JSON
      frameResponse.body &&
        new Blob([frameResponse.body], {
          type: 'application/json',
        }),
      responseInit,
    );

    return response;
  };

  return fetchOverride;
};
